import { useTranslation } from "react-i18next";
import { useArchiveDialog } from "@/lib/inbox";
import { pushToast } from "@/components/Toaster";

/**
 * Archive a dialog — or bring it back, with `unarchive` — and show an
 * undo-toast (DESIGN-SYSTEM §6.4 — reversible destructive actions get an
 * undo-toast, never a silent irreversible op). Shared by InboxPage and
 * InboxDrawer so the behaviour is identical.
 *
 * Returns a function: archive(dialogId, { onArchived, unarchive }).
 * `onArchived` lets the caller reset its own selection state on success.
 */
export function useArchiveWithUndo() {
  const { t } = useTranslation();
  const archive = useArchiveDialog();

  return (
    dialogId: string,
    opts: { onArchived?: () => void; unarchive?: boolean } = {},
  ) => {
    const unarchive = opts.unarchive ?? false;
    archive.mutate(
      { dialogId, unarchive },
      {
        onSuccess: () => {
          opts.onArchived?.();
          pushToast({
            kind: "success",
            text: t(unarchive ? "inbox.unarchived" : "inbox.archived"),
            durationMs: 8000,
            action: {
              label: t("inbox.undo"),
              onClick: () => archive.mutate({ dialogId, unarchive: !unarchive }),
            },
          });
        },
        onError: () => {
          pushToast({ kind: "error", text: t("inbox.archive_failed") });
        },
      },
    );
  };
}
